import * as v from 'valibot';

const getValAtPath = (target: unknown, path: string[]): unknown =>
  path.reduce((result, key) => (result as Record<string, unknown>)?.[key], target);

const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Creates a custom validation action that checks for equivalence between two values of
 * a typed object given their keys
 *
 * @param {string} antecedentKey Property key
 * @param {string} dependentKey Another property key
 * @param {string} [message] Optional error message
 *
 * @returns {CheckAction}
 */
export const valuesEquivalent = <TInput extends Record<string, unknown>>(
  antecedentKey: string,
  dependentKey: string,
  message?: string
) => {
  const defaultMessage = `${capitalize(antecedentKey)} and ${dependentKey} must be equivalent`;

  return v.check((input: TInput) => {
    const antecedent = getValAtPath(input, [antecedentKey]);
    const dependent = getValAtPath(input, [dependentKey]);
    return antecedent === dependent;
  }, message || defaultMessage);
};

/**
 * Creates a custom validation action that checks for equivalence between two properties of a
 * typed object given their arbitrarily deep dot paths.
 *
 * @param {string[]} antecedentPath Arbitrarily deep path to property
 * @param {string[]} dependentPath Arbitrarily deep path to another property
 * @param {string} [message] Optional error message
 *
 * @returns {CheckAction}
 */
export const propsEquivalent = <TInput extends Record<string, unknown>>(
  antecedentPath: string[],
  dependentPath: string[],
  message?: string
) => {
  const defaultMessage = `
    ${capitalize(antecedentPath.join('.'))} and ${dependentPath.join('.')} must be equivalent
  `;

  return v.check((input: TInput) => {
    const antecedent = getValAtPath(input, antecedentPath);
    const dependent = getValAtPath(input, dependentPath);
    return antecedent === dependent;
  }, message || defaultMessage);
};

/**
 * Creates a custom validation action that checks if dependent condition is true
 * when antecedent condition is true (logical implication: A → B)
 *
 * @param {(input: TInput) => boolean} antecedentRequirement Function that returns true/false
 * @param {(input: TInput) => boolean} dependentRequirement Function that returns true/false
 * @param {string} [message] Optional error message
 *
 * @returns {CheckAction}
 */
export const conditionalCheck = <TInput extends Record<string, unknown>>(
  antecedentRequirement: (input: TInput) => boolean,
  dependentRequirement: (input: TInput) => boolean,
  message?: string
) => {
  const defaultMessage = 'Dependent condition must be true when antecedent condition is true';

  return v.check((input: TInput) => {
    const antecedentCondition = antecedentRequirement(input);
    const dependentCondition = dependentRequirement(input);
    return !antecedentCondition || dependentCondition; // !A || B is equivalent to A → B
  }, message || defaultMessage);
};

/**
 * Creates a custom validation action that checks if dependent property is not nullish
 * when antecedent property is not nullish
 *
 * @param {string[]} antecedentPath Arbitrarily deep path to antecedent property
 * @param {string[]} dependentPath Arbitrarily deep path to dependent property
 * @param {string} [message] Optional error message
 *
 * @returns {CheckAction}
 */
export const conditionalPropRequired = <TInput extends Record<string, unknown>>(
  antecedentPath: string[],
  dependentPath: string[],
  message?: string
) => {
  const defaultMessage = `
    ${capitalize(dependentPath.join('.'))} is required when ${antecedentPath.join('.')} is provided
  `;

  return v.check((input: TInput) => {
    const antecedent = getValAtPath(input, antecedentPath);
    const dependent = getValAtPath(input, dependentPath);
    return (antecedent ?? null) === null || (dependent ?? null) !== null;
  }, message || defaultMessage);
};
